import { ELocalStorageKeys } from '@/lib/enums';
import { getEnvVariable } from '@/lib/getEnvVariable';
import { webStorageUtil } from '@/state/webStorage.state.service';

/**
 * Browsers cap the bytes a document may have outstanding in keepalive
 * requests at 64 KB per origin. Anything bigger is sent as an ordinary fetch
 * rather than being rejected outright.
 */
export const KEEPALIVE_BODY_LIMIT_BYTES = 60 * 1024;

/**
 * Publisher events fire at the moments a portal document is most likely to
 * die: right after login, on the way back to the game, inside a webview that
 * is about to be hidden and killed. An XHR is aborted with the document; a
 * keepalive fetch that has already been handed to the browser completes
 * regardless. That is the difference between the store and the portal in how
 * many `login.completed` events reach the publisher.
 *
 * Exactly one request per event, no retry: a rejected fetch cannot tell "never
 * sent" from "sent, response lost", and a second copy of a login event is
 * worse for the publisher than a missing one.
 */
export const postPublisherEvent = async (path: string, data: unknown): Promise<void> => {
  const body = JSON.stringify(data);
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers: buildHeaders(),
    body,
    keepalive: fitsKeepaliveBudget(body),
  });

  if (!response.ok) {
    throw new Error(`Request failed with status code ${response.status}`);
  }
};

const buildUrl = (path: string): string => {
  const baseUrl =
    getEnvVariable(
      'NEXT_PUBLIC_REACT_APP_API_BASE_URL',
      process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL
    ) ?? '';

  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
};

const buildHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = webStorageUtil.getAny(ELocalStorageKeys.SESSION_TOKEN);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const fitsKeepaliveBudget = (body: string): boolean =>
  new TextEncoder().encode(body).byteLength <= KEEPALIVE_BODY_LIMIT_BYTES;
